import { useMemo } from "react"
import { useEditor } from "@tiptap/react"
import { createEditorConfig } from "@/tiptap/core/create-editor"
import { handleImageUpload, MAX_FILE_SIZE } from "@/tiptap/lib/tiptap-utils"
import { useStableCallback } from "@/tiptap/hooks/use-stable-callback"
import flash from "@/lib/flashes"

const DEFAULT_IMAGE_UPLOAD = {
  maxSize: MAX_FILE_SIZE,
  limit: 3,
  onError: (error) => flash(error?.message || "Upload failed", "error"),
}

const DEFAULT_TABLE_OPTIONS = {
  resizable: true,
  cellMinWidth: 35,
}

/**
 * Isolates Tiptap editor creation and lifecycle in a single hook.
 * Keeps the editor instance out of parent component state to avoid
 * unnecessary re-renders.
 *
 * @param {Object} options
 * @param {string}  options.content        - Initial HTML content
 * @param {string}  [options.placeholder]  - Placeholder text
 * @param {Function} [options.onChange]     - Called with serialized HTML on every update
 * @param {Function} [options.onTocUpdate] - Called when table-of-contents changes
 * @param {Object}  [options.imageUpload]  - Override default image upload config
 * @param {string}  [options.recordType]   - Owning record type used to tag image uploads
 * @param {string}  [options.recordId]     - Owning record id used to tag image uploads
 * @param {Object}  [options.tableOptions] - Override default table options
 * @param {string}  [options.editorClassName] - CSS class for the editor element
 * @returns {import('@tiptap/react').Editor | null}
 */
export function useEditorInstance({
  content,
  placeholder = "Start writing...",
  onChange,
  onTocUpdate,
  imageUpload,
  recordType = "",
  recordId = "",
  tableOptions,
  editorClassName,
} = {}) {
  const stableOnChange = useStableCallback(onChange)
  const stableOnTocUpdate = useStableCallback(onTocUpdate)
  const defaultImageUpload = useMemo(
    () => ({
      ...DEFAULT_IMAGE_UPLOAD,
      upload: (file, onProgress, abortSignal) =>
        handleImageUpload(file, onProgress, abortSignal, { recordType, recordId }),
    }),
    [recordType, recordId],
  )
  const editorConfig = useMemo(
    () =>
      createEditorConfig({
        content,
        placeholder,
        onChange: stableOnChange,
        onTocUpdate: stableOnTocUpdate,
        imageUpload: imageUpload ?? defaultImageUpload,
        tableOptions: tableOptions ?? DEFAULT_TABLE_OPTIONS,
        editorClassName,
      }),
    [
      content,
      placeholder,
      stableOnChange,
      stableOnTocUpdate,
      imageUpload,
      defaultImageUpload,
      tableOptions,
      editorClassName,
    ],
  )

  const editor = useEditor(editorConfig)

  return editor
}

export default useEditorInstance
